"use client";

import { useEffect, useRef, useState } from "react";

export interface FeedbackSubmission {
  description: string;
  pathname: string;
}

export function FeedbackWidget({
  pathname,
  onSubmit,
}: {
  pathname: string;
  onSubmit: (submission: FeedbackSubmission) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [description, setDescription] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitting = useRef(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open && !sent) textareaRef.current?.focus();
  }, [open, sent]);

  const close = () => {
    if (submitting.current) return;
    setOpen(false);
    setSent(false);
    setError(null);
    triggerRef.current?.focus();
  };

  return (
    // Pinned bottom bars mark themselves with data-feedback-clearance; sit above them.
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-2 [body:has([data-feedback-clearance])_&]:bottom-[calc(47px+1.25rem)]">
      {open && (
        <section
          role="dialog"
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.stopPropagation();
              close();
            }
          }}
          aria-label="Give feedback"
          className="w-[340px] max-w-[calc(100vw-2.5rem)] overflow-hidden rounded-(--radius-container) border border-(--border-subtle) bg-(--bg-white) shadow-lg"
        >
          <header className="flex items-center justify-between border-b border-(--divider-row) px-4 py-3">
            <h2 className="text-[13px] font-semibold text-(--fg-primary)">
              Give feedback
            </h2>
            <button
              type="button"
              onClick={close}
              aria-label="Close feedback"
              className="text-(--fg-muted) hover:text-(--fg-primary)"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                aria-hidden={true}
              >
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </header>

          {sent ? (
            <div className="px-4 py-6 text-center">
              <p className="text-[13px] font-medium text-(--fg-primary)">
                Thanks, this is on its way.
              </p>
              <p className="mt-1 text-[12px] text-(--fg-secondary)">
                The Elion team will review it within one business day.
              </p>
              <button
                type="button"
                onClick={close}
                className="mt-4 rounded-(--radius-field) border border-(--border-subtle) bg-(--bg-white) px-3 py-1.5 text-[12px] font-medium text-(--fg-primary) hover:bg-(--bg-hover)"
              >
                Done
              </button>
            </div>
          ) : (
            <form
              className="flex flex-col gap-3 px-4 py-3.5"
              onSubmit={async (event) => {
                event.preventDefault();
                if (!description.trim() || submitting.current) return;
                submitting.current = true;
                setPending(true);
                setError(null);
                try {
                  await onSubmit({ description: description.trim(), pathname });
                  setSent(true);
                  setDescription("");
                } catch (error) {
                  setError(
                    error instanceof Error
                      ? error.message
                      : "Unable to send feedback. Please try again.",
                  );
                } finally {
                  submitting.current = false;
                  setPending(false);
                }
              }}
            >
              <p className="text-[12px] leading-[1.5] text-(--fg-secondary)">
                Tell us what looks off, whether it's research data that doesn't
                add up or a problem with the app. We'll include your current
                page and the Elion team will review it within one business day.
              </p>
              <label className="flex flex-col gap-1">
                <span className="sr-only">What are you seeing?</span>
                <textarea
                  ref={textareaRef}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  rows={4}
                  maxLength={5000}
                  disabled={pending}
                  placeholder="What are you seeing?"
                  className="w-full resize-none rounded-(--radius-field) border border-(--border-subtle) bg-(--bg-white) px-3 py-2 text-[12.5px] text-(--fg-primary) outline-none placeholder:text-(--fg-muted) focus:border-(--border-strong)"
                />
              </label>
              <div className="flex items-center gap-1.5 text-[11px] text-(--fg-muted)">
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden={true}
                >
                  <rect x="3" y="4" width="18" height="14" rx="2" />
                  <path d="M3 9h18" />
                </svg>
                <span className="truncate">Page: {pathname}</span>
              </div>
              {error && (
                <p
                  role="alert"
                  className="text-[12px] text-(--status-error-text)"
                >
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={pending || !description.trim()}
                className="rounded-(--radius-field) bg-(--btn-primary-bg) px-3 py-2 text-[12px] font-semibold text-(--btn-primary-fg) transition-opacity hover:opacity-90 disabled:opacity-40"
              >
                {pending ? "Sending…" : "Send to Elion"}
              </button>
            </form>
          )}
        </section>
      )}

      <button
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        ref={triggerRef}
        disabled={pending}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-(--radius-full) bg-(--btn-primary-bg) px-3.5 py-2 text-[12.5px] font-semibold text-(--btn-primary-fg) shadow-lg transition-opacity hover:opacity-90"
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden={true}
        >
          <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8A8.5 8.5 0 0 1 12.5 3 8.5 8.5 0 0 1 21 11.5z" />
        </svg>
        Give feedback
      </button>
    </div>
  );
}
